import { injectable } from 'tsyringe';
import type { PlanSummary } from '@bmai/shared';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import {
    OperatorTenantsService,
    type EntitlementInput,
    type OperatorEntitlement,
    type OperatorTenant,
} from '../OperatorTenantsService';
import { OperatorHealthService, type AccountHealthRow } from '../OperatorHealthService';
import { OperatorPlansService, type QuotaBucket } from '../plans/OperatorPlansService';
import { OperatorTenantUsersService, type InviteTenantUserInput, type TenantUser } from './OperatorTenantUsersService';

/**
 * Backing model for the operator tenant drill-down. Composes the tenant
 * lifecycle/entitlement, account-health, billing (plans + quotas), and
 * tenant-user services into one per-tenant view with Overview / Usage / Users /
 * Management tabs.
 */
@injectable()
export class OperatorTenantDetailViewModel extends BaseViewModel {
    tenantId = '';
    tenant: OperatorTenant | null = null;
    health: AccountHealthRow | null = null;
    entitlement: OperatorEntitlement | null = null;
    quotas: QuotaBucket[] | null = null;
    users: TenantUser[] | null = null;
    plans: PlanSummary[] = [];

    loading = true;
    error: string | null = null;
    busy = false;
    /** User ids with an action in flight — per-row spinners in the Users tab. */
    busyUsers = new Set<string>();

    constructor(
        private readonly tenants: OperatorTenantsService,
        private readonly healthService: OperatorHealthService,
        private readonly plansService: OperatorPlansService,
        private readonly usersService: OperatorTenantUsersService,
    ) {
        super();
    }

    async load(tenantId: string): Promise<void> {
        this.tenantId = tenantId;
        this.loading = true;
        this.error = null;
        this.notifyChange();
        try {
            const [tenants, health, entitlement, quotas, users, plans] = await Promise.all([
                this.tenants.listTenants(),
                this.healthService.listAccountHealth().catch(() => [] as AccountHealthRow[]),
                this.tenants.getEntitlement(tenantId).catch(() => null),
                this.plansService.getTenantQuotas(tenantId).catch(() => [] as QuotaBucket[]),
                this.usersService.list(tenantId).catch(() => [] as TenantUser[]),
                this.plansService.list().catch(() => [] as PlanSummary[]),
            ]);
            this.tenant = tenants.find((t) => t.id === tenantId) ?? null;
            this.health = health.find((h) => h.tenantId === tenantId) ?? null;
            this.entitlement = entitlement;
            this.quotas = quotas;
            this.users = users;
            this.plans = plans;
            if (!this.tenant) this.error = `Tenant ${tenantId} not found`;
        } catch (e) {
            this.error = e instanceof Error ? e.message : 'Failed to load tenant';
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    private async run(action: () => Promise<void>): Promise<void> {
        this.busy = true;
        this.notifyChange();
        try {
            await action();
            await this.load(this.tenantId);
        } finally {
            this.busy = false;
            this.notifyChange();
        }
    }

    private async runUser(userId: string, action: () => Promise<void>): Promise<void> {
        this.busyUsers.add(userId);
        this.notifyChange();
        try {
            await action();
            this.users = await this.usersService.list(this.tenantId);
        } finally {
            this.busyUsers.delete(userId);
            this.notifyChange();
        }
    }

    isUserBusy(userId: string): boolean {
        return this.busyUsers.has(userId);
    }

    // — Management —
    assignPlan(planId: string): Promise<void> {
        return this.run(() => this.plansService.assignPlan(this.tenantId, planId));
    }

    saveEntitlement(input: EntitlementInput): Promise<void> {
        return this.run(() => this.tenants.saveEntitlement(this.tenantId, input));
    }

    suspend(reason?: string): Promise<void> {
        return this.run(() => this.tenants.suspend(this.tenantId, reason));
    }

    resume(): Promise<void> {
        return this.run(() => this.tenants.resume(this.tenantId));
    }

    offboard(reason?: string): Promise<void> {
        return this.run(() => this.tenants.offboard(this.tenantId, reason));
    }

    // — Users —
    async inviteUser(input: InviteTenantUserInput): Promise<void> {
        await this.usersService.invite(this.tenantId, input);
        this.users = await this.usersService.list(this.tenantId);
        this.notifyChange();
    }

    setUserEnabled(userId: string, enabled: boolean): Promise<void> {
        return this.runUser(userId, () => this.usersService.setEnabled(this.tenantId, userId, enabled));
    }

    deleteUser(userId: string): Promise<void> {
        return this.runUser(userId, () => this.usersService.remove(this.tenantId, userId));
    }

    async resendInvite(userId: string, kind: 'invite' | 'reset'): Promise<{ emailSent: boolean }> {
        this.busyUsers.add(userId);
        this.notifyChange();
        try {
            return await this.usersService.resendInvite(this.tenantId, userId, kind);
        } finally {
            this.busyUsers.delete(userId);
            this.notifyChange();
        }
    }
}
